import { useState } from 'react';
import { X, Split } from 'lucide-react';
import { posOrders } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';
import ManagerApprovalModal from './ManagerApprovalModal';

export default function SplitBillModal({ order, onClose, onSplit, requireApproval = false }) {
  const { posToken } = usePosStore();
  const [billCount, setBillCount] = useState(2);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showApproval, setShowApproval] = useState(false);
  const [pendingSplits, setPendingSplits] = useState(null);

  const activeItems = order?.items?.filter((i) => !i.voidedAt) || [];

  const assign = (itemId, billIndex) => {
    setAssignments((prev) => ({ ...prev, [itemId]: billIndex }));
  };

  const buildSplits = () => {
    const splits = Array.from({ length: billCount }, () => ({ items: [] }));
    for (const item of activeItems) {
      const bill = assignments[item._id] ?? 0;
      splits[bill].items.push({ itemId: item._id, quantity: item.quantity });
    }
    return splits.filter((s) => s.items.length > 0);
  };

  const handleRequestSplit = () => {
    setError(null);
    const splits = buildSplits();
    if (splits.length < 2) {
      setError('At least 2 bills must have items assigned');
      return;
    }
    if (requireApproval) {
      setPendingSplits(splits);
      setShowApproval(true);
    } else {
      executeSplit(splits, null);
    }
  };

  const executeSplit = async (splits, approvedBy) => {
    setLoading(true);
    setError(null);
    try {
      const res = await posOrders.split(posToken, order._id, { splits, approvedBy });
      onSplit(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Split failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApproved = (approvedBy) => {
    setShowApproval(false);
    executeSplit(pendingSplits, approvedBy);
  };

  const billColors = [
    'bg-blue-50 border-blue-300',
    'bg-green-50 border-green-300',
    'bg-yellow-50 border-yellow-300',
    'bg-purple-50 border-purple-300',
  ];

  const billBHasItems = activeItems.some((item) => (assignments[item._id] ?? 0) !== 0);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              Split Bill — {order?.tableName}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-medium text-gray-700">Bills:</label>
            {[2, 3, 4].map((n) => (
              <button
                key={n}
                onClick={() => setBillCount(n)}
                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                  billCount === n
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-2 mb-5">
            <p className="text-xs text-gray-500 mb-2">
              All items start in Bill 1. Tap a number to reassign.
            </p>
            {activeItems.map((item) => (
              <div key={item._id} className="flex items-center justify-between gap-2">
                <span className="text-sm text-gray-800 flex-1">
                  {item.quantity}× {item.name}
                  <span className="text-xs text-gray-500 ml-1">
                    ₱{(item.subtotal / 100).toFixed(2)}
                  </span>
                </span>
                <div className="flex gap-1">
                  {Array.from({ length: billCount }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => assign(item._id, i)}
                      className={`w-7 h-7 rounded-full border text-xs font-bold transition-colors ${
                        (assignments[item._id] ?? 0) === i
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300 text-gray-600 hover:border-blue-400'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {Array.from({ length: billCount }, (_, i) => {
              const billItems = activeItems.filter((item) => (assignments[item._id] ?? 0) === i);
              const total = billItems.reduce((sum, item) => sum + item.subtotal, 0);
              return (
                <div key={i} className={`p-3 rounded-lg border ${billColors[i]}`}>
                  <p className="text-xs font-bold mb-1">Bill {i + 1}</p>
                  <p className="text-xs text-gray-600">{billItems.length} item(s)</p>
                  <p className="text-sm font-semibold">₱{(total / 100).toFixed(2)}</p>
                </div>
              );
            })}
          </div>

          <button
            onClick={handleRequestSplit}
            disabled={loading || !billBHasItems}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Split className="h-4 w-4" />
            {loading
              ? 'Splitting…'
              : requireApproval
              ? 'Request Approval'
              : `Split into ${billCount} bills`}
          </button>
        </div>
      </div>

      <ManagerApprovalModal
        isOpen={showApproval}
        action="split_bill"
        onApprove={handleApproved}
        onClose={() => setShowApproval(false)}
      />
    </>
  );
}
